import { CONFIG } from '../utils/config';
import { getDiscoverAppsQueryBody } from '../utils/helper';
import { apiSlice } from './apiSlice';

// Token storage keys
const TOKEN_STORAGE_KEY = 'enterprise_search_token';
const TOKEN_EXPIRY_STORAGE_KEY = 'enterprise_search_token_expiry';

// Token fetch controller (still in memory as AbortController can't be serialized)
let tokenFetchController = null;

// Helper function to get token (reusable across endpoints)
async function getEnterpriseToken() {
  const currentTime = Date.now();
  // Try to get cached token from localStorage
  const cachedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);

  // Return cached token if still valid
  if (cachedToken && tokenExpiry && currentTime < parseInt(tokenExpiry, 10)) {
    return cachedToken;
  }

  // Cancel previous token fetch if still pending
  if (tokenFetchController) {
    tokenFetchController.abort();
  }

  tokenFetchController = new AbortController();

  try {
    const tokenResponse = await fetch(`${CONFIG.ENTERPRISE_SEARCH_API}/token`, {
      method: 'POST',
      headers: {
        source: 'adp.com'
      }
    });

    if (!tokenResponse.ok) {
      throw new Error(`Token fetch failed: ${tokenResponse.status}`);
    }

    const tokenData = await tokenResponse.json();
    const newToken = tokenData.access_token;
    const newExpiry = currentTime + (tokenData.expires_in || 3600) * 1000;

    // Store token and expiry in localStorage
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, newExpiry.toString());

    return newToken;
  } finally {
    tokenFetchController = null;
  }
}

export const homepageApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // User profile endpoint
    getUserProfile: builder.query({
      query: () => ({ url: '/user/profile', method: 'GET' }),
      providesTags: ['User'],
      transformErrorResponse: (response) => {
        console.error('User not authenticated or API unavailable');
        return null;
      }
    }),
    // Product tags endpoint
    getProductTags: builder.query({
      query: () => ({
        url: '/api/product/tags',
        method: 'GET'
      }),
      providesTags: ['ProductTags'],
      transformResponse: (response) => {
        // Expected backend shape (new):
        // - [{ attributeGroups: [...] }, ...]
        // Some environments may wrap payloads in { data: ... }.
        console.log('[Solutions Debug] getProductTags raw response:', response, Array.isArray(response) ? 'Array' : typeof response);

        if (Array.isArray(response)) return response;

        if (Array.isArray(response?.data)) return response.data;

        // Defensive: handle single-document responses
        if (response?.attributeGroups) return [response];
        if (response?.data?.attributeGroups) return [response.data];

        return [];
      },
      transformErrorResponse: (response) => {
        console.error('Failed to fetch product tags:', response?.data || 'API Error');
        return [];
      }
    }),
    getUserEntitlements: builder.query({
      query: () => ({
        url: '/api/company/memberships',
        method: 'GET'
      }),
      providesTags: ['Memberships'],
      transformErrorResponse: (response) => {
        console.error('fetchCompanyMemberships is failing:', response?.data || 'API Error');
        return [];
      }
    }),

    // Products by attribute endpoint
    getProductsByAttribute: builder.query({
      query: ({ attributeId, count = 3 }) => ({
        url: `/api/v2/products?attributeIds=[${attributeId}]&count=${count}`,
        method: 'GET'
      }),
      providesTags: (result, error, { attributeId }) => [{ type: 'Products', id: attributeId }],
      // DEBUG: log whenever products for a given attribute (Solutions category) are fetched
      transformResponse: (response, meta, arg) => {
        // Normalize to always return { products: [] }
        const products =
          Array.isArray(response)
            ? response
            : Array.isArray(response?.products)
              ? response.products
              : Array.isArray(response?.data?.products)
                ? response.data.products
                : [];

        console.log('[Solutions Debug] getProductsByAttribute normalized:', {
          attributeId: arg?.attributeId,
          count: arg?.count,
          productsCount: products.length,
          raw: response
        });

        return { products };
      },
      transformErrorResponse: (response) => {
        console.error('Failed to fetch products by attribute:', response?.data || 'API Error');
        return [];
      }
    }),

    // Get Enterprise Search Token
    getEnterpriseSearchToken: builder.query({
      queryFn: async () => {
        try {
          const token = await getEnterpriseToken();
          return { data: token };
        } catch (error) {
          console.error('Error fetching token:', error.name === 'AbortError' ? 'Request aborted' : error.message);
          return {
            error: {
              status: error.status || 500,
              data: error.message
            }
          };
        }
      },
      providesTags: ['SearchToken']
    }),

    // Discover Apps from Marketplace API
    getDiscoverApps: builder.query({
      queryFn: async ({ discoveryParam = 'Bestsellers', platformName = null } = {}) => {
        try {
          // Get token (cached or fresh)
          const token = await getEnterpriseToken();

          // Fetch discover apps
          const url = `${CONFIG.ENTERPRISE_SEARCH_API}/search?documentType=MKP`;
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              associateoid: '',
              orgoid: '',
              publisher: 'ADPMKP',
              source: 'adp.com',
              sm_transactionID: `cmp${Date.now()}`,
              sm_serversessionid: `cmp${Date.now()}`,
              Authorization: `bearer ${token}`,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(getDiscoverAppsQueryBody(discoveryParam, platformName))
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const products = data?.response?.docs || data?.hits?.hits || data?.products || data?.data || [];

          return { data: products };
        } catch (error) {
          console.error('❌ Failed to fetch discover apps:', error.name === 'AbortError' ? 'Request aborted' : error.message);
          return {
            error: {
              status: error.status || 500,
              data: error.message
            }
          };
        }
      },
      providesTags: ['DiscoverApps']
    }),

    // CMS Home page content API
    // URL base varies per environment: see REACT_APP_CMS_API_URL in each .env file.
    // Backend returns { success, data: { platforms, banners, discoverNewApps, ... } }
    getHomepageContent: builder.query({
      query: () => ({
        url: `${CONFIG.CMS_API_URL}/home`,
        method: 'GET',
        headers: { source: 'adp.com' }
      }),
      providesTags: ['HomeContent'],
      transformResponse: (response) => response?.data ?? null,
      transformErrorResponse: (response) => {
        console.error('Failed to fetch homepage content:', response?.data || 'API Error');
        return null;
      }
    })

  })
});

export const {
  useGetUserProfileQuery,
  useGetHomepageContentQuery,
  useGetUserEntitlementsQuery,
  useGetProductTagsQuery,
  useGetProductsByAttributeQuery,
  // useGetPlatformsQuery, // DEPRECATED: Platforms now included in getHomepageContent
  useGetEnterpriseSearchTokenQuery,
  useGetDiscoverAppsQuery
} = homepageApi;
